import { useEffect, useState } from 'react';
import HeroFloatingCards from './HeroFloatingCards';

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, []);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const stats = [
    { value: '7 dní', label: 'a web je live' },
    { value: '50+', label: 'spokojných klientov' },
    { value: '+150 %', label: 'priemerný rast' },
    { value: '100 %', label: 'osobná zodpovednosť' },
  ];

  return (
    <section
      id="uvod"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-zinc-950"
    >
      {/* BACKGROUND */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div
          className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.12) 0%, transparent 60%)' }}
        />
        <div
          className="absolute -bottom-20 right-0 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.07) 0%, transparent 65%)' }}
        />
        <div
          className="absolute inset-0 opacity-[0.028]"
          style={{
            backgroundImage: 'linear-gradient(rgba(249,115,22,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.6) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
        <div className="absolute bottom-0 inset-x-0 h-36 bg-gradient-to-t from-zinc-950 to-transparent" />
      </div>

      {/* MAIN CONTENT */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-16">

        <div className="flex flex-col lg:flex-row lg:items-center lg:gap-14 xl:gap-20">

          {/* LEFT */}
          <div className="flex-1 lg:max-w-[50%]">

            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 bg-zinc-900/80 border border-orange-500/20 rounded-full px-3.5 py-1.5 mb-7"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(10px)',
                transition: 'opacity 0.55s ease, transform 0.55s ease',
              }}
            >
              <span className="relative flex w-2 h-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-60" />
                <span className="relative inline-flex rounded-full w-2 h-2 bg-orange-500" />
              </span>
              <span className="text-orange-300 text-xs sm:text-sm font-medium">Web ktorý predáva - hotový za 7 dní</span>
            </div>

            {/* Headline - single, crystal clear */}
            <div
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(22px)',
                transition: 'opacity 0.65s ease, transform 0.65s ease',
                transitionDelay: '0.1s',
              }}
            >
              <h1 className="text-[2.6rem] sm:text-5xl md:text-6xl lg:text-[3.6rem] xl:text-[4rem] font-black text-white leading-[1.04] tracking-tight mb-4">
                Kým konkurencia čaká,
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
                  vy predávate.
                </span>
              </h1>
            </div>

            {/* Sub - short, benefit-driven */}
            <p
              className="text-zinc-400 text-base sm:text-lg max-w-lg mb-9 leading-relaxed"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(12px)',
                transition: 'opacity 0.65s ease, transform 0.65s ease',
                transitionDelay: '0.22s',
              }}
            >
              Moderné weby, e-shopy a kampane - odovzdané{' '}
              <strong className="text-white font-semibold">rýchlejšie</strong> ako tradičné agentúry.
              Každý projekt odovzdávam osobne, s plnou zodpovednosťou za výsledok.
            </p>

            {/* CTA */}
            <div
              className="flex flex-col sm:flex-row gap-3 mb-9"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(12px)',
                transition: 'opacity 0.65s ease, transform 0.65s ease',
                transitionDelay: '0.33s',
              }}
            >
              <button
                onClick={() => handleScroll('#kontakt')}
                className="group relative inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white font-bold px-8 py-4 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-base overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative">Chcem web za 7 dní</span>
                <i className="ri-arrow-right-line relative transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => handleScroll('#referencie')}
                className="inline-flex items-center justify-center gap-2 border border-zinc-700/60 hover:border-orange-500/35 text-zinc-300 hover:text-white font-medium px-7 py-4 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer text-base bg-zinc-900/50 hover:bg-orange-500/5"
              >
                Pozrieť ukážky
                <i className="ri-external-link-line text-sm text-zinc-600" />
              </button>
            </div>

            {/* Social proof strip */}
            <div
              className="flex flex-wrap items-center gap-x-6 gap-y-3"
              style={{
                opacity: visible ? 1 : 0,
                transition: 'opacity 0.65s ease',
                transitionDelay: '0.44s',
              }}
            >
              {/* Avatars + rating */}
              <div className="flex items-center gap-3">
                <div className="flex">
                  {[31, 32, 33, 34].map((seq, i) => (
                    <div
                      key={seq}
                      className="w-8 h-8 rounded-full overflow-hidden border-2 border-zinc-900"
                      style={{ marginLeft: i === 0 ? 0 : '-8px', zIndex: 4 - i }}
                    >
                      <img
                        src={`https://readdy.ai/api/search-image?query=professional%20portrait%20headshot%20smiling%20confident%20person%20clean%20background%20warm%20lighting%20business%20casual&width=60&height=60&seq=hpav${seq}&orientation=squarish`}
                        alt="klient"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className="ri-star-fill text-orange-400 text-xs" />
                    ))}
                    <span className="text-white text-xs font-bold ml-1">4.9</span>
                  </div>
                  <p className="text-zinc-500 text-xs">50+ klientov</p>
                </div>
              </div>

              <div className="w-px h-8 bg-zinc-800 hidden sm:block" />

            </div>


          </div>

          {/* RIGHT - visual */}
          <div
            className="hidden lg:block flex-shrink-0 w-full lg:w-[46%] xl:w-[44%]"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(28px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease',
              transitionDelay: '0.4s',
            }}
          >
            <HeroFloatingCards />
          </div>
        </div>

        {/* STATS ROW */}
        <div
          className="mt-12 sm:mt-16 grid grid-cols-2 md:grid-cols-4 border border-zinc-800/50 rounded-2xl overflow-hidden bg-zinc-900/40"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(14px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
            transitionDelay: '0.6s',
          }}
        >
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`flex flex-col items-center text-center px-4 sm:px-6 py-5 sm:py-7
                ${i < 3 ? 'border-r border-zinc-800/50' : ''}
                ${i < 2 ? 'border-b md:border-b-0 border-zinc-800/50' : ''}`}
            >
              <span className="text-3xl sm:text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-white to-zinc-300 leading-none mb-1.5">
                {stat.value}
              </span>
              <span className="text-zinc-500 text-[11px] sm:text-xs font-medium">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* SCROLL */}
      <div
        className="hidden sm:flex absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2 cursor-pointer"
        onClick={() => handleScroll('#sluzby')}
        style={{ opacity: visible ? 1 : 0, transition: 'opacity 1s ease', transitionDelay: '1.1s' }}
      >
        <span className="text-zinc-700 text-[10px] tracking-widest uppercase">scroll</span>
        <div className="w-5 h-8 border border-zinc-700/60 rounded-full flex items-start justify-center pt-1.5">
          <div className="w-1 h-1.5 rounded-full bg-orange-400" style={{ animation: 'driftUp 1.6s ease-in-out infinite' }} />
        </div>
      </div>
    </section>
  );
}
